/**
 * BG Progress Carousel — background image carousel with progress bar navigation.
 */
class BgProgressCarousel extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.isAnimating = false;
    /** @type {HTMLElement[]} */
    this.pcSlides = [];
    /** @type {HTMLElement[]} */
    this.overlayPanels = [];
    /** @type {HTMLElement[]} */
    this.heroPanels = [];
    /** @type {HTMLElement[]} */
    this.moCopies = [];
    /** @type {HTMLButtonElement[]} */
    this.pcTriggers = [];
    /** @type {HTMLButtonElement[]} */
    this.moTriggers = [];
    this.progressRaf = null;
    this.progressStart = 0;
    this.isInView = false;
    this.intersectionObserver = null;

    this._onPcTriggerClick = this._onPcTriggerClick.bind(this);
    this._onMoTriggerClick = this._onMoTriggerClick.bind(this);
    this._onTabRowScroll = this._onTabRowScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._tickProgress = this._tickProgress.bind(this);
  }

  connectedCallback() {
    this._unbind();

    this.autoEnabled = this.dataset.enableAutoPlay === 'true';
    this.autoDuration = Math.max(2, Number.parseInt(this.dataset.autoPlayDuration || '10', 10)) * 1000;
    this.loop = this.dataset.loop === 'true';
    this.slideCount = Number.parseInt(this.dataset.slideCount || '0', 10);

    this.track = this.querySelector('[data-bg-progress-track]');
    this.row = this.querySelector('[data-bg-progress-row]');
    this.overlayHost = this.querySelector('[data-bg-progress-overlay-host]');
    this.heroesHost = this.querySelector('[data-bg-progress-heroes]');
    this.moCopiesHost = this.querySelector('[data-bg-progress-mo-copies]');
    this.tabRow = this.querySelector('.bg-progress-carousel__tab-row');
    this.tabIndicator = this.querySelector('[data-bg-progress-tab-indicator]');
    this.tabRule = this.querySelector('.bg-progress-carousel__tab-rule');
    this.pcTriggers = Array.from(this.querySelectorAll('[data-bg-progress-trigger]'));
    this.moTriggers = Array.from(this.querySelectorAll('[data-bg-progress-mo-trigger]'));

    if (!this.track) return;

    this._collectSlides();
    this._loadPictureBreakpoints(this.heroesHost);

    const domSlideCount = this.track.querySelectorAll('[data-bg-progress-slide]').length;
    if (domSlideCount > 0) {
      this.slideCount = domSlideCount;
    }

    if (this.slideCount < 1 || (!this.pcSlides.length && !this.heroPanels.length)) return;

    this._syncProgressLabels();

    this.pcTriggers.forEach((btn) => {
      btn.addEventListener('click', this._onPcTriggerClick);
    });
    this.moTriggers.forEach((btn) => {
      btn.addEventListener('click', this._onMoTriggerClick);
    });
    this.tabRow?.addEventListener('scroll', this._onTabRowScroll);
    window.addEventListener('resize', this._onResize);

    if (this.autoEnabled && this.slideCount > 1) {
      this._setupObserver();
    }

    this.setActive(0, false);
  }

  disconnectedCallback() {
    this._unbind();
  }

  _unbind() {
    this._stopProgress();
    this._teardownObserver();
    this.pcTriggers.forEach((btn) => {
      btn.removeEventListener('click', this._onPcTriggerClick);
    });
    this.moTriggers.forEach((btn) => {
      btn.removeEventListener('click', this._onMoTriggerClick);
    });
    this.tabRow?.removeEventListener('scroll', this._onTabRowScroll);
    window.removeEventListener('resize', this._onResize);
  }

  /**
   * Collect slide fragments from rendered blocks.
   */
  _collectSlides() {
    const items = Array.from(this.track.querySelectorAll('[data-bg-progress-slide]'));

    items.forEach((item, index) => {
      const mediaPc = item.querySelector('[data-bg-progress-media-pc]');
      const mediaMo = item.querySelector('[data-bg-progress-media-mo]');
      const overlayPc = item.querySelector('[data-bg-progress-overlay-pc]');
      const copyMo = item.querySelector('[data-bg-progress-copy-mo]');

      if (mediaPc && this.row) {
        const slide = document.createElement('div');
        slide.className = 'bg-progress-carousel__slide-pc';
        slide.dataset.slideIndex = String(index);
        slide.appendChild(mediaPc);
        this.row.appendChild(slide);
        this.pcSlides.push(slide);
      }

      if (overlayPc && this.overlayHost) {
        const panel = document.createElement('div');
        panel.className = 'bg-progress-carousel__overlay-panel';
        panel.dataset.slideIndex = String(index);
        panel.appendChild(overlayPc);
        this.overlayHost.appendChild(panel);
        this.overlayPanels.push(panel);
      }

      if (mediaMo && this.heroesHost) {
        const heroPanel = document.createElement('div');
        heroPanel.className = 'bg-progress-carousel__hero-panel';
        heroPanel.dataset.slideIndex = String(index);

        const hero = document.createElement('div');
        hero.className = 'bg-progress-carousel__hero';
        hero.appendChild(mediaMo);
        heroPanel.appendChild(hero);

        this.heroesHost.appendChild(heroPanel);
        this.heroPanels.push(heroPanel);
      }

      if (copyMo && this.moCopiesHost) {
        const copyPanel = document.createElement('div');
        copyPanel.className = 'bg-progress-carousel__copy-mo';
        copyPanel.dataset.slideIndex = String(index);
        copyPanel.appendChild(copyMo);
        this.moCopiesHost.appendChild(copyPanel);
        this.moCopies.push(copyPanel);
      }
    });
  }

  /**
   * Eagerly mount lazy picture-breakpoint nodes after the custom element is defined.
   * Section JS loads before picture-breakpoint-image-lazy.js, so a direct loadImage()
   * call during DOM relocation is often a no-op.
   * @param {ParentNode | null} root
   */
  _loadPictureBreakpoints(root) {
    if (!root) return;

    const load = () => {
      root.querySelectorAll('picture-breakpoint').forEach((element) => {
        if (element.dataset.mounted !== 'true' && typeof element.loadImage === 'function') {
          element.loadImage();
        }
      });
    };

    if (customElements.get('picture-breakpoint')) {
      load();
      return;
    }

    customElements.whenDefined('picture-breakpoint').then(load);
  }

  /**
   * Theme blocks may expose empty settings in section loops — sync labels from slide data.
   */
  _syncProgressLabels() {
    const items = Array.from(this.track.querySelectorAll('[data-bg-progress-slide]'));

    this.pcTriggers.forEach((btn, index) => {
      const title = items[index]?.dataset.bgProgressTitle;
      const label = btn.querySelector('.bg-progress-carousel__progress-label');
      if (label && title) label.textContent = title;
    });

    this.moTriggers.forEach((btn, index) => {
      const title = items[index]?.dataset.bgProgressTitle;
      if (title) btn.textContent = title;
    });
  }

  /**
   * @returns {boolean}
   */
  _isDesktop() {
    return window.matchMedia('(min-width: 1280px)').matches;
  }

  _setupObserver() {
    if (this.intersectionObserver) return;

    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.isInView = true;
          this._startProgress();
        } else {
          this.isInView = false;
          this._stopProgress();
        }
      }
    }, { threshold: 0.1 });

    this.intersectionObserver.observe(this);
  }

  _teardownObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  _startProgress() {
    if (!this.autoEnabled || this.slideCount < 2) return;
    if (this.progressRaf != null) return;

    this.progressStart = performance.now();
    this.progressRaf = requestAnimationFrame(this._tickProgress);
  }

  _stopProgress() {
    if (this.progressRaf != null) {
      cancelAnimationFrame(this.progressRaf);
      this.progressRaf = null;
    }
  }

  _tickProgress() {
    const elapsed = performance.now() - this.progressStart;
    const ratio = Math.min(elapsed / this.autoDuration, 1);
    this._setProgressFill(this.activeIndex, ratio);

    if (ratio >= 1) {
      this.progressRaf = null;
      this._goToNext(true);
      return;
    }

    this.progressRaf = requestAnimationFrame(this._tickProgress);
  }

  /**
   * @param {boolean} fromAuto
   */
  _goToNext(fromAuto = false) {
    if (this.slideCount < 2) return;

    let nextIndex = this.activeIndex + 1;

    if (nextIndex >= this.slideCount) {
      if (!this.loop) return;
      nextIndex = 0;
    }

    const instantReset = fromAuto && this.loop && this.activeIndex === this.slideCount - 1 && nextIndex === 0;
    this.setActive(nextIndex, true, instantReset);
  }

  /**
   * @param {Event} event
   */
  _onPcTriggerClick(event) {
    const btn = event.currentTarget;
    if (!(btn instanceof HTMLButtonElement)) return;

    const index = Number.parseInt(btn.dataset.bgProgressTrigger || '', 10);
    if (Number.isNaN(index) || index === this.activeIndex) return;

    this.setActive(index, true, false);
  }

  /**
   * @param {Event} event
   */
  _onMoTriggerClick(event) {
    const btn = event.currentTarget;
    if (!(btn instanceof HTMLButtonElement)) return;

    const index = Number.parseInt(btn.dataset.bgProgressMoTrigger || '', 10);
    if (Number.isNaN(index) || index === this.activeIndex) return;

    this.setActive(index, true, false);
  }

  _onTabRowScroll() {
    this._moveTabIndicator(this.activeIndex);
  }

  _onResize() {
    this._setRowOffset(this.activeIndex, false);
    this._moveTabIndicator(this.activeIndex);
  }

  /**
   * @param {number} index
   * @param {number} ratio
   */
  _setProgressFill(index, ratio) {
    const fill = this.pcTriggers[index]?.querySelector('[data-bg-progress-fill]');
    if (!fill) return;

    const percent = `${Math.max(0, Math.min(ratio, 1)) * 100}%`;
    fill.style.setProperty('--bg-progress-fill', percent);
  }

  /**
   * Sync progress bar fills for the current state.
   */
  _syncProgressFills() {
    this.pcTriggers.forEach((_, index) => {
      let ratio = 0;
      if (index === this.activeIndex && !this.autoEnabled) {
        ratio = 1;
      }
      this._setProgressFill(index, ratio);
    });
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   */
  _setRowOffset(index, animate) {
    if (!this.row) return;

    this.row.classList.toggle('is-transitioning', animate);
    this.row.style.transform = `translate3d(${-index * 100}%, 0, 0)`;
  }

  /**
   * @param {number} fromIndex
   * @param {number} toIndex
   * @param {boolean} animate
   */
  _animateOverlay(fromIndex, toIndex, animate) {
    const toPanel = this.overlayPanels[toIndex];
    if (!toPanel) return;

    if (fromIndex === toIndex) {
      this.overlayPanels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === toIndex;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);
        panel.classList.remove('is-entering', 'is-leaving');
      });
      return;
    }

    const fromPanel = this.overlayPanels[fromIndex];
    if (!fromPanel) return;

    if (!animate) {
      fromPanel.hidden = true;
      fromPanel.classList.remove('is-active', 'is-entering', 'is-leaving');
      toPanel.hidden = false;
      toPanel.classList.add('is-active');
      toPanel.classList.remove('is-entering', 'is-leaving');
      return;
    }

    fromPanel.classList.add('is-leaving');
    fromPanel.classList.remove('is-entering');
    toPanel.hidden = false;
    toPanel.classList.add('is-entering', 'is-active');
    toPanel.classList.remove('is-leaving');

    const duration = 500;

    window.setTimeout(() => {
      fromPanel.hidden = true;
      fromPanel.classList.remove('is-active', 'is-leaving');
      toPanel.classList.remove('is-entering');
    }, duration);
  }

  /**
   * @param {number} index
   */
  _moveTabIndicator(index) {
    if (!this.tabIndicator || !this.tabRow || !this.tabRule) return;
    if (this._isDesktop()) {
      this.tabIndicator.setAttribute('hidden', '');
      return;
    }

    const tab = this.moTriggers[index];
    if (!tab) return;

    this.tabIndicator.removeAttribute('hidden');
    const ruleRect = this.tabRule.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    this.tabIndicator.style.width = `${tabRect.width}px`;
    this.tabIndicator.style.left = `${tabRect.left - ruleRect.left}px`;
  }

  /**
   * @param {number} index
   * @param {boolean} animate
   * @param {boolean} instantReset
   */
  setActive(index, animate = true, instantReset = false) {
    if (index < 0 || index >= this.slideCount) return;
    if (this.isAnimating && animate) return;

    const previousIndex = this.activeIndex;
    this.activeIndex = index;

    if (instantReset) {
      this._setRowOffset(index, false);
      requestAnimationFrame(() => {
        this._setRowOffset(index, false);
      });
    } else {
      this._setRowOffset(index, animate);
    }

    this._animateOverlay(previousIndex, index, animate);

    this.heroPanels.forEach((panel, panelIndex) => {
      if (panelIndex === index) {
        panel.removeAttribute('hidden');
        this._loadPictureBreakpoints(panel);
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.moCopies.forEach((panel, panelIndex) => {
      if (panelIndex === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.pcTriggers.forEach((btn, btnIndex) => {
      btn.setAttribute('aria-selected', btnIndex === index ? 'true' : 'false');
    });

    this.moTriggers.forEach((btn, btnIndex) => {
      btn.setAttribute('aria-selected', btnIndex === index ? 'true' : 'false');
    });

    if (animate) {
      this.isAnimating = true;
      window.setTimeout(() => {
        this.isAnimating = false;
      }, 500);
    }

    this._stopProgress();
    this._syncProgressFills();

    if (this.autoEnabled && this.slideCount > 1 && this.isInView) {
      this._startProgress();
    }

    if (!this._isDesktop() && this.tabRow) {
      const activeTab = this.moTriggers[index];
      if (activeTab) {
        const targetLeft = activeTab.offsetLeft - (this.tabRow.clientWidth / 2) + (activeTab.offsetWidth / 2);
        const maxLeft = this.tabRow.scrollWidth - this.tabRow.clientWidth;
        this.tabRow.scrollTo({
          left: Math.max(0, Math.min(targetLeft, maxLeft)),
          behavior: animate ? 'smooth' : 'auto',
        });
        const finalize = () => this._moveTabIndicator(index);
        this.tabRow.addEventListener('scrollend', finalize, { once: true });
        window.setTimeout(finalize, animate ? 450 : 360);
        return;
      }
    }

    this._moveTabIndicator(index);
  }
}

if (!customElements.get('bg-progress-carousel')) {
  customElements.define('bg-progress-carousel', BgProgressCarousel);
}
